import hoistStatics from 'hoist-non-react-statics';
import { observer } from 'mobx-react';
import React from 'react';
import { CoursePriceStore, PriceStatus } from '@udemy/common-stores';
import { captureException } from '@udemy/sentry';
import { getDisplayName } from '@udemy/shared-utils';
import { withStores } from '@udemy/store-provider';
import { SpecialBundlePriceBadge } from '../special-bundle-price-badge/special-bundle-price-badge.react-component';
import styles from "./with-course-price-store-base.module.css";

// Props injected by the HOC into the wrapped component

// Props to constrain those required of the wrapped component by the HOC

/**
 * Props for the WithCoursePriceStore higher-order component.
 *
 * @privateRemarks
 *
 * The HOC will inject the props in InjectedProps into the wrapped component, so
 * we mark them as optional for external consumers.
 */

export function withCoursePriceStoreBase(LoadingComponent, ErrorComponent) {
  return function withDeferredCoursePrice(WrappedPriceText) {
    class InternalAsyncPriceText extends React.Component {
      constructor(props) {
        super(props);
        this.coursePriceStore = void 0;
        this.state = {
          showBundlePriceBadge: false,
          discountedPriceMap: new Map()
        };
        this.coursePriceStore = props.stores[0];

        // Register courses to the course price store as normal
        props.courses.forEach(course => this.registerCourse(course));
      }
      componentDidMount() {
        // If the bundle discount is enabled, fetch the discounted course prices
        if (this.props.isBundleDiscountEnabled && this.props.bundleDiscountCode) {
          this.fetchDiscountedCoursePrices(this.props.bundleDiscountCode);
        }
      }
      componentDidUpdate(prevProps) {
        if (this.props.isBundleDiscountEnabled && this.props.bundleDiscountCode && prevProps.isBundleDiscountEnabled !== this.props.isBundleDiscountEnabled) {
          this.fetchDiscountedCoursePrices(this.props.bundleDiscountCode);
        }
      }
      registerCourse(course) {
        if (course.price) {
          const initialPrice = {
            price: course.price,
            price_detail: course.price_detail,
            discount: course.discount,
            discount_price: course.discount_price
          };
          this.coursePriceStore.registerCourse(course, initialPrice);
        } else {
          this.coursePriceStore.registerCourse(course);
        }
      }
      fetchDiscountedCoursePrices(discountCode) {
        const {
          courses
        } = this.props;
        const courseIds = courses.map(course => course.id);
        this.coursePriceStore.getDiscountedCoursePrices(courseIds, discountCode).then(response => {
          let isDiscountCodeApplied = false;

          // Check whether any course in the response has the discount code applied
          response.forEach(pricingResult => {
            if (pricingResult.code && pricingResult.code === discountCode) {
              isDiscountCodeApplied = true;
            }
          });

          // If neither course has a discount code applied, register the courses
          // to the course price store as normal to get the default pricing.
          if (!isDiscountCodeApplied) {
            courses.forEach(course => this.registerCourse(course));
          }
          this.setState({
            discountedPriceMap: response,
            showBundlePriceBadge: isDiscountCodeApplied && this.props.isBundleDiscountEnabled
          });
        }).catch(e => {
          captureException(e);
        });
      }
      getPriceAmountsFromCoursePrice(coursePrice) {
        var _coursePrice$discount;
        const courseListPrice = coursePrice !== null && coursePrice !== void 0 && coursePrice.price_detail ? coursePrice.price_detail.amount : 0;
        const courseDiscountPrice = coursePrice !== null && coursePrice !== void 0 && (_coursePrice$discount = coursePrice.discount) !== null && _coursePrice$discount !== void 0 && _coursePrice$discount.price ? coursePrice.discount.price.amount : 0;
        return {
          courseListPrice,
          courseDiscountPrice
        };
      }
      getPriceStringsFromCoursePrice(coursePrice) {
        var _coursePrice$discount2;
        const courseListPriceString = coursePrice !== null && coursePrice !== void 0 && coursePrice.price_detail ? coursePrice.price_detail.price_string : undefined;
        const courseDiscountPriceString = coursePrice !== null && coursePrice !== void 0 && (_coursePrice$discount2 = coursePrice.discount) !== null && _coursePrice$discount2 !== void 0 && _coursePrice$discount2.price ? coursePrice.discount.price.price_string : undefined;
        return {
          courseListPriceString,
          courseDiscountPriceString
        };
      }
      render() {
        const {
          courses,
          loaderProps,
          ...restOfProps
        } = this.props;
        const {
          showBundlePriceBadge
        } = this.state;

        // When displaying a special bundle price, use the bundle price map in state
        // instead of the default CoursePriceStore price map.
        const priceMap = showBundlePriceBadge ? this.state.discountedPriceMap : this.coursePriceStore.priceMap;
        const hasError = courses.some(course => {
          const price = priceMap.get(course.id);
          return (price === null || price === void 0 ? void 0 : price.status) === PriceStatus.PRICE_STATUS_ERROR;
        });
        if (hasError) {
          return /*#__PURE__*/React.createElement(ErrorComponent, {
            className: this.props.className
          });
        }
        const isLoading = courses.some(course => {
          const price = priceMap.get(course.id);
          return !price || price.status === PriceStatus.PRICE_STATUS_LOADING;
        });
        if (isLoading) {
          return /*#__PURE__*/React.createElement(LoadingComponent, Object.assign({}, loaderProps, {
            className: this.props.className
          }));
        }
        let listPrice = 0;
        let discountPrice = 0;
        courses.forEach(course => {
          // Cast to CoursePrice after checking for loading state
          const currentCoursePrice = priceMap.get(course.id);
          const {
            courseListPrice,
            courseDiscountPrice
          } = this.getPriceAmountsFromCoursePrice(currentCoursePrice);
          listPrice += courseListPrice !== null && courseListPrice !== void 0 ? courseListPrice : 0;
          if (courseDiscountPrice) {
            discountPrice += courseDiscountPrice;
          } else {
            discountPrice += courseListPrice !== null && courseListPrice !== void 0 ? courseListPrice : 0;
          }
        });
        const priceTextProps = {
          listPrice,
          discountPrice
        };
        if (courses.length === 1) {
          const coursePrice = this.coursePriceStore.priceMap.get(courses[0].id);
          const {
            courseListPriceString,
            courseDiscountPriceString
          } = this.getPriceStringsFromCoursePrice(coursePrice);
          priceTextProps.listPriceString = courseListPriceString;
          priceTextProps.discountPriceString = courseDiscountPriceString || courseListPriceString;
          const loadedCoursePrice = coursePrice;
          priceTextProps.trackingEventContext = loadedCoursePrice !== null && loadedCoursePrice !== void 0 && loadedCoursePrice.price_serve_tracking_id ? {
            priceServeTrackingId: loadedCoursePrice === null || loadedCoursePrice === void 0 ? void 0 : loadedCoursePrice.price_serve_tracking_id
          } : undefined;
        }

        // When displaying a special bundle price, we cannot show a strikethrough.
        // Therefore, we set the list price to the discount price and set the discount price to 0.
        if (showBundlePriceBadge) {
          priceTextProps.showDiscountPriceOnly = true;
          const originalTrackingProps = priceTextProps.trackingEventContext;
          priceTextProps.trackingEventContext = {
            ...originalTrackingProps,
            isSpecialBundlePriceActive: true
          };
        }
        function renderWrappedPriceText() {
          return /*#__PURE__*/React.createElement(WrappedPriceText, Object.assign({
            courses: courses
          }, priceTextProps, restOfProps));
        }
        return showBundlePriceBadge ? /*#__PURE__*/React.createElement("div", {
          className: styles['wrapped-price-text-container']
        }, renderWrappedPriceText(), showBundlePriceBadge && /*#__PURE__*/React.createElement(SpecialBundlePriceBadge, null)) : renderWrappedPriceText();
      }
    }
    InternalAsyncPriceText.defaultProps = {
      courses: [],
      className: '',
      loaderProps: {},
      isBundleDiscountEnabled: false,
      bundleDiscountCode: ''
    };
    InternalAsyncPriceText.displayName = `WithCourse${getDisplayName(WrappedPriceText)}`;
    const AsyncPriceText = withStores([CoursePriceStore], observer(InternalAsyncPriceText));
    return hoistStatics(AsyncPriceText, WrappedPriceText);
  };
}
//# sourceMappingURL=with-course-price-store-base.js.map